<script setup lang="ts">
import {
    updateFavouriteLocation,
    updateMultipleFavouriteLocations,
    type Directus_Favourite_Location,
} from '@/api/calls/backend-calls/favourite-locations-calls'
import type FavouriteLocation from '@/classes/favouriteLocation'
import AlertPopupWindow from '@/components/alert-popup/AlertPopupWindow.vue'
import GenericDialog from '@/components/dialog-wrappers/GenericDialog.vue'
import ElevatedBtn from '@/components/ui-elements/buttons/ElevatedBtn.vue'
import OutlinedBtn from '@/components/ui-elements/buttons/OutlinedBtn.vue'
import TextBtn from '@/components/ui-elements/buttons/TextBtn.vue'
import LoadingCard from '@/components/ui-elements/loaders/LoadingCard.vue'
import { MutationTypes, type State } from '@/store/store_types'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import RequestCard from './RequestCard.vue'

const store = useStore<State>()
const pendingRequests = computed(() =>
  store.state.favLocations.filter((loc) => loc.approvalStatus === 'pending'),
)
const approvedRequests = computed(() => {
  const approved_history_directus_ids = store.state.approval_history
  if (!approved_history_directus_ids.length) return []
  return store.state.favLocations.filter(
    (loc) => loc.directusId && approved_history_directus_ids.includes(loc.directusId),
  )
})
const default_msg =
  'Are you sure you want to approve/deny all these selected requests? You can remove these locations later from your fleet locations.'
const loading_msg = 'Processing your updates...'
const msg = computed(() => {
  if (async_in_progress.value) return loading_msg
  if (outcome_msg.value) return outcome_msg.value
  return default_msg
})

// ---- multi approval/reject helpers ---- //

const toApproveSelection = ref<string[]>([])
const toRejectSelection = ref<string[]>([])

const addToApproveSelection = (id: string) => {
  if (!toApproveSelection.value.includes(id)) {
    toApproveSelection.value.push(id)
  }

  removeFromRejectSelection(id)
}

const removeFromApproveSelection = (id: string) => {
  toApproveSelection.value = toApproveSelection.value.filter((item) => item !== id)
}

const addToRejectSelection = (id: string) => {
  if (!toRejectSelection.value.includes(id)) {
    toRejectSelection.value.push(id)
  }
  removeFromApproveSelection(id)
}

const removeFromRejectSelection = (id: string) => {
  toRejectSelection.value = toRejectSelection.value.filter((item) => item !== id)
}

const clearSelection = (id: string) => {
  removeFromApproveSelection(id)
  removeFromRejectSelection(id)
}

const showMultiApproveDenyDialog = ref(false)
const DSA = computed(() => store.state.DSA_multi_approve_deny_warning)
const toggleDSA = () => {
  store.commit(MutationTypes.setDSA_multi_approve_deny_warning, !DSA.value)
}
const handle_approve_deny = () => {
  if (DSA.value) {
    approve_deny() // skip warning dialog if don't show again has been set
    return
  }
  showMultiApproveDenyDialog.value = true
}

const success_msg = 'The selected requests have been updated successfully'
const partial_success_msg =
  'Some of the selected request have been updated, however some had errors'
const error_msg = 'An error occurred while trying to update these request'
const outcome_msg = ref<
  typeof success_msg | typeof partial_success_msg | typeof error_msg | undefined
>(undefined)
const async_in_progress = ref(false)
const approve_deny = async () => {
  // indicate async action is in progress
  async_in_progress.value = true
  // locate fav locations that match selection
  const approved_Fav_locations: FavouriteLocation[] = pendingRequests.value.filter((fl) =>
    toApproveSelection.value.includes(fl.localId),
  )
  const deny_fav_locations: FavouriteLocation[] = pendingRequests.value.filter((fl) =>
    toRejectSelection.value.includes(fl.localId),
  )
  // prep obj for updates
  const approval_obj: Partial<Directus_Favourite_Location> = {
    approval_status: 'approved',
    Visibility: 'fleet',
  }
  const deny_obj: Partial<Directus_Favourite_Location> = {
    approval_status: null,
  }
  // update DB
  let approval_outcome: 'ok' | 'error' | undefined = undefined
  if (approved_Fav_locations.length) {
    const directus_ids = approved_Fav_locations
      .map((fl) => fl.directusId)
      .filter((x): x is number => x !== undefined)
    const approval_res = await updateMultipleFavouriteLocations(directus_ids, approval_obj)
    // check results
    if (!approval_res) {
      approval_outcome = 'error'
    } else {
      approval_outcome = 'ok'
      // update relevant objects in the store
      approved_Fav_locations.forEach((fl) => {
        fl.visibility = 'fleet'
        fl.approvalStatus = 'approved'
        store.commit(MutationTypes.updateIndividualFavLocation, fl)
      })
      store.commit(MutationTypes.set_approval_history, [
        ...store.state.approval_history,
        ...directus_ids,
      ])
      // clear to approve selection
      toApproveSelection.value = []
    }
  }
  let deny_outcome: 'ok' | 'error' | undefined = undefined
  if (deny_fav_locations.length) {
    const deny_res = await updateMultipleFavouriteLocations(
      deny_fav_locations.map((fl) => fl.directusId).filter((x): x is number => x !== undefined),
      deny_obj,
    )
    // check results
    if (!deny_res) {
      deny_outcome = 'error'
    } else {
      deny_outcome = 'ok'
      // update relevant objects in the store
      deny_fav_locations.forEach((fl) => {
        fl.approvalStatus = undefined
        store.commit(MutationTypes.updateIndividualFavLocation, fl)
      })
      // clear to deny selection
      toRejectSelection.value = []
    }
  }

  // indicate outcome to the user

  // full error cases
  if (approval_outcome === 'error' && !deny_outcome) {
    // case: full error as no deny request
    outcome_msg.value = error_msg
  }
  if (!approval_outcome && deny_outcome === 'error') {
    // case: full error as no approval request
    outcome_msg.value = error_msg
  }
  if (approval_outcome === 'error' && deny_outcome === 'error') {
    // case: full error
    outcome_msg.value = error_msg
  }
  // partial error cases
  if (approval_outcome === 'ok' && deny_outcome === 'error') {
    // case: approval success, deny failure
    outcome_msg.value = partial_success_msg
  }
  if (approval_outcome === 'error' && deny_outcome === 'ok') {
    // case: approval failure, deny success
    outcome_msg.value = partial_success_msg
  }
  // success cases
  if (approval_outcome === 'ok' && !deny_outcome) {
    // case: approval success, no deny request
    outcome_msg.value = success_msg
  }
  if (!approval_outcome && deny_outcome === 'ok') {
    // case: no approval request, deny success
    outcome_msg.value = success_msg
  }
  if (approval_outcome === 'ok' && deny_outcome === 'ok') {
    // case: full success
    outcome_msg.value = success_msg
  }

  // indicate async action has finished
  async_in_progress.value = false
}

const clean_up_multi = () => {
  async_in_progress.value = false
  outcome_msg.value = undefined
}

// ---- individual request approve/deny handling ---- //

const individual_async_in_progress = ref(false)
const show_individual_modal = ref(false)
const individual_request_id = ref<string | undefined>(undefined)
const individual_request_data = computed(() =>
  pendingRequests.value.find(
    (fl) => individual_request_id.value && fl.localId === individual_request_id.value,
  ),
)
const handle_show_individual_request = (id: string) => {
  individual_request_id.value = id
  show_individual_modal.value = true
}

const individual_outcome = ref<'approved' | 'denied' | 'error' | undefined>(undefined)
const update_request = async (outcome: 'approve' | 'deny') => {
  individual_async_in_progress.value = true
  if (!individual_request_data.value || !individual_request_data.value?.directusId) {
    individual_outcome.value = 'error'
    individual_async_in_progress.value = false
    return
  }
  if (outcome === 'approve') {
    const res = await updateFavouriteLocation(individual_request_data.value.directusId, {
      approval_status: 'approved',
      Visibility: 'fleet',
    })
    if (!res) {
      individual_outcome.value = 'error'
      individual_async_in_progress.value = false
      return
    }
    individual_request_data.value.visibility = 'fleet'
    individual_request_data.value.approvalStatus = 'approved'
    store.commit(MutationTypes.updateIndividualFavLocation, individual_request_data.value)
    if (individual_request_data.value.directusId)
      store.commit(MutationTypes.add_approval_history, individual_request_data.value.directusId)
    individual_outcome.value = 'approved'
    individual_async_in_progress.value = false
    return
  }
  if (outcome === 'deny') {
    const res = await updateFavouriteLocation(individual_request_data.value.directusId, {
      approval_status: null,
    })
    if (!res) {
      individual_outcome.value = 'error'
      individual_async_in_progress.value = false
      return
    }
    individual_request_data.value.approvalStatus = undefined
    store.commit(MutationTypes.updateIndividualFavLocation, individual_request_data.value)
    individual_outcome.value = 'denied'
    individual_async_in_progress.value = false
    return
  }
  individual_outcome.value = 'error'
  individual_async_in_progress.value = false
}

const clean_up_individual = () => {
  individual_request_id.value = undefined
  individual_outcome.value = undefined
}

// ---- request approval history removal ---- //
const show_delete_history_confirmation = ref(false)
const marked_for_delete_id = ref<string | null>(null)
const marked_for_delete_data = computed(() => {
  if (!marked_for_delete_id.value) return undefined
  return approvedRequests.value.find((fl) => fl.localId === marked_for_delete_id.value)
})
const DSA__delete = computed(() => store.state.DSA_request_history_delete_confirmation)
const toggle_DSA__delete = () => {
  store.commit(MutationTypes.setDSA_request_history_delete_confirmation, !DSA__delete.value)
}
const mark_for_delete = (id: string) => {
  marked_for_delete_id.value = id
  if(DSA__delete.value) {
    handle_delete_history()
  } else {
    show_delete_history_confirmation.value = true
  }
}
const handle_delete_history = async () => {
  if (!marked_for_delete_data.value || !marked_for_delete_data.value?.directusId) return
  store.commit(MutationTypes.remove_approval_history, marked_for_delete_data.value.directusId)
}
const clean_up_delete = () => {
  marked_for_delete_id.value = null
} 
</script>

<template>
  <div class="bg-pwt_snow pa-5">
    <div class="text-h6 d-flex flex-nowrap align-center justify-space-between">
      <span> Fleet locations requests </span>
      <div>
        <TextBtn> Sort by </TextBtn>
        <TextBtn> Filter </TextBtn>
      </div>
    </div>

    <v-row no-gutters class="my-5">
      <v-col
        v-for="(request, index) in pendingRequests"
        :key="'requested-location-card-' + request.localId"
        :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-md-2'"
        class="mb-3"
        cols="12"
        md="6"
      >
        <RequestCard
          :requestedLocation="request"
          @flag-approved="addToApproveSelection"
          @flag-rejected="addToRejectSelection"
          @flag-cleared="clearSelection"
          @click="handle_show_individual_request"
        />
      </v-col>
    </v-row>
    <v-row no-gutters class="mb-10">
      <v-spacer></v-spacer>
      <ElevatedBtn
        v-if="toApproveSelection.length > 0 || toRejectSelection.length > 0"
        shape="pill"
        @click="handle_approve_deny"
      >
        Approve / deny
      </ElevatedBtn>
    </v-row>

    <div class="text-h6 d-flex flex-nowrap align-center justify-space-between">
      <span> Approved requests </span>
      <div>
        <TextBtn> Sort by </TextBtn>
        <TextBtn> Filter </TextBtn>
      </div>
    </div>

    <v-row no-gutters class="mb-10 mt-5">
      <v-col
        v-for="(request, index) in approvedRequests"
        :key="'location-card-' + index"
        :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-md-2'"
        class="mb-3"
        cols="12"
        md="6"
      >
        <RequestCard :requestedLocation="request" @history-delete="mark_for_delete" />
      </v-col>
    </v-row>
  </div>

  <!-- multi approval/deny dialog -->
  <AlertPopupWindow
    :activator="showMultiApproveDenyDialog"
    title="Approve/deny these requests"
    :message="msg"
    :show-loading-animation="async_in_progress"
    :display-do-not-show-again-input="!outcome_msg"
    :show-actions="!outcome_msg && !async_in_progress"
    @do-not-show-again="toggleDSA"
    @show-again="toggleDSA"
    @continue="approve_deny"
    @close="() => (showMultiApproveDenyDialog = false)"
    @after-leave="clean_up_multi"
  />
  <!-- individual approval/deny dialog -->
  <GenericDialog
    :activator="show_individual_modal"
    @close="() => (show_individual_modal = false)"
    @after-leave="clean_up_individual"
    :small="!!individual_outcome || individual_async_in_progress"
  >
    <v-card flat class="pa-10" v-if="individual_async_in_progress">
      <v-card-title class="text-center"> Processing ... </v-card-title>
      <v-card-text class="d-flex align-center justify-center">
        <LoadingCard :size="60" />
      </v-card-text>
    </v-card>

    <v-card flat class="pa-10" v-else-if="individual_outcome === 'approved'">
      <v-card-title class="text-center"> Request approved </v-card-title>
      <v-card-text class="text-center">
        You have approved {{ individual_request_data?.requestedByUser }}’s request.
        {{ individual_request_data?.address }} is now saved to fleet locations.
      </v-card-text>
    </v-card>

    <v-card flat class="pa-10" v-else-if="individual_outcome === 'denied'">
      <v-card-title class="text-center"> Request denied </v-card-title>
      <v-card-text class="text-center">
        You have denied {{ individual_request_data?.requestedByUser }}’s request
      </v-card-text>
    </v-card>

    <v-card flat class="pa-10" v-else-if="individual_outcome === 'error'">
      <v-card-title class="text-center"> An error occurred </v-card-title>
      <v-card-text class="text-center">
        An error has occurred while attempting to update this request.
      </v-card-text>
    </v-card>

    <v-card flat class="pa-10" v-else>
      <v-card-title class="text-center mb-10">
        {{ individual_request_data?.address }}
      </v-card-title>
      <v-card-text class="text-center" style="min-height: 35vh">
        {{ individual_request_data?.requestedByUser }} has requested to add
        <span class="text-decoration-underline"> {{ individual_request_data?.address }} </span> to
        saved fleet locations.
      </v-card-text>
      <v-card-actions class="justify-center">
        <OutlinedBtn @click="() => update_request('deny')"> Deny </OutlinedBtn>
        <ElevatedBtn @click="() => update_request('approve')"> Approve </ElevatedBtn>
      </v-card-actions>
    </v-card>
  </GenericDialog>
  <!-- delete request approval history modal -->
  <AlertPopupWindow 
    :activator="show_delete_history_confirmation"
    title="Delete this from history?"
    message="Are you sure you want to delete this approved request from your history? This won’t remove it from your fleet’s locations."
    @close="() => show_delete_history_confirmation = false"
    @after-leave="clean_up_delete"
    @continue="handle_delete_history"
    @do-not-show-again="toggle_DSA__delete"
    @show-again="toggle_DSA__delete"
  />
</template>
